import { useMutation } from '@tanstack/react-query';
import { Col, Row } from 'antd';
import { useTitle } from 'contexts/TitleProvider';
import { useUser } from 'contexts/UserProvider';
import dayjs from 'dayjs';
import useQueryParam from 'hooks/useQueryPrams';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { ButtonExport, ButtonFilter } from 'components';
import { DEFAULT_PAGE_SIZE } from 'utils/constants/constant';
import { PaymentInfoApis } from 'apis/PaymentInfoApis';
import FilterPaymentInfo from './FilterPaymentInfo';
import TablePaymentInfo from './TablePaymentInfo';

const PaymentInfoPage = () => {
  const { user } = useUser();
  const childRef = useRef<any>(null);
  const navigate = useNavigate();
  const { setTitle, setBreadcrumb } = useTitle();

  const [data, setData] = useState<any>([]);
  const [isOpenFilter, setIsOpenFilter] = useState(false);
  const [formParams, setFormParams] = useState<any>({});
  const [exportLoading, setExportLoading] = useState(false);

  const queryParam = useQueryParam();
  const page = parseInt(queryParam.get('page') + '') || 1;
  const page_size =
    parseInt(queryParam.get('page_size') + '') || DEFAULT_PAGE_SIZE;

  const defaultFilter = useMemo(() => {
    return {
      fromDateToDate: [dayjs().subtract(1, 'month'), dayjs()],
      plantId: (user as any)?.plantCode ?? undefined,
    };
  }, [user]);

  const mutateGetReport = useMutation({
    mutationKey: ['getPaymentInfoReport'],
    mutationFn: (params: any) => PaymentInfoApis.getPaymentInfoReport(params),
    onSuccess: (res: any) => {
      const result = res?.data || { items: [], pageInfo: {} };
      setData(result);
    },
  });

  useEffect(() => {
    setTitle('Thông tin thanh toán');
    setBreadcrumb([
      { title: 'Kế toán/Thủ quỹ' },
      { title: 'Thông tin thanh toán' },
    ]);
  }, [setTitle, setBreadcrumb]);

  const refetchData = useCallback(() => {
    try {
      const filters = {
        ...defaultFilter,
        ...formParams,
      };

      const fromDateVal = filters.fromDateToDate?.[0]
        ? dayjs(filters.fromDateToDate[0]).startOf('day').toISOString()
        : undefined;
      const toDateVal = filters.fromDateToDate?.[1]
        ? dayjs(filters.fromDateToDate[1]).endOf('day').toISOString()
        : undefined;

      const fromPaymentDateVal = filters.fromPaymentDateToPaymentDate?.[0]
        ? dayjs(filters.fromPaymentDateToPaymentDate[0])
            .startOf('day')
            .toISOString()
        : undefined;
      const toPaymentDateVal = filters.fromPaymentDateToPaymentDate?.[1]
        ? dayjs(filters.fromPaymentDateToPaymentDate[1])
            .endOf('day')
            .toISOString()
        : undefined;

      mutateGetReport.mutate({
        page,
        limit: page_size,
        plantId: filters.plantId,
        id: filters.id,
        billingCode: filters.billingCode,
        customerId: filters.customerId,
        fromDate: fromDateVal,
        toDate: toDateVal,
        fromPaymentDate: fromPaymentDateVal,
        toPaymentDate: toPaymentDateVal,
        soPo: filters.soPo,
        orderType: filters.orderType,
        transactionCode: filters.transactionCode,
        dmsOrderCode: filters.dmsOrderCode,
      });
    } catch (error) {
      console.log('refetchData', error);
    }
  }, [page, page_size, formParams, defaultFilter, mutateGetReport]);

  useEffect(() => {
    refetchData();
  }, [page, page_size, formParams]);

  const handleExportExcel = async () => {
    try {
      setExportLoading(true);
      const filters = {
        ...defaultFilter,
        ...formParams,
      };

      const fromDateVal = filters.fromDateToDate?.[0]
        ? dayjs(filters.fromDateToDate[0]).startOf('day').toISOString()
        : undefined;
      const toDateVal = filters.fromDateToDate?.[1]
        ? dayjs(filters.fromDateToDate[1]).endOf('day').toISOString()
        : undefined;

      const fromPaymentDateVal = filters.fromPaymentDateToPaymentDate?.[0]
        ? dayjs(filters.fromPaymentDateToPaymentDate[0])
            .startOf('day')
            .toISOString()
        : undefined;
      const toPaymentDateVal = filters.fromPaymentDateToPaymentDate?.[1]
        ? dayjs(filters.fromPaymentDateToPaymentDate[1])
            .endOf('day')
            .toISOString()
        : undefined;

      const res = await PaymentInfoApis.exportPaymentInfoExcel({
        plantId: filters.plantId,
        id: filters.id,
        billingCode: filters.billingCode,
        customerId: filters.customerId,
        fromDate: fromDateVal,
        toDate: toDateVal,
        fromPaymentDate: fromPaymentDateVal,
        toPaymentDate: toPaymentDateVal,
        soPo: filters.soPo,
        orderType: filters.orderType,
        transactionCode: filters.transactionCode,
        dmsOrderCode: filters.dmsOrderCode,
      });

      const blob = new Blob([res], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `Thong_Tin_Thanh_Toan_${dayjs().format('YYYYMMDDHHmmss')}.xlsx`,
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export excel error', error);
    } finally {
      setExportLoading(false);
    }
  };

  const handleOkFilter = useCallback(
    (values: any) => {
      navigate(`?page=1&page_size=${page_size}`);
      setFormParams(values);
    },
    [navigate, page_size],
  );

  const handleCancelFilter = useCallback(() => {
    setFormParams({});
  }, []);

  return (
    <>
      <Row gutter={[8, 16]} align={'middle'} justify={'end'}>
        <Col>
          <ButtonFilter isOpen={isOpenFilter} setIsOpen={setIsOpenFilter} />
        </Col>
        <Col>
          <ButtonExport
            onClick={handleExportExcel}
            loading={exportLoading}
            disabled={!data?.items?.length}
          />
        </Col>
      </Row>

      <FilterPaymentInfo
        isOpen={isOpenFilter}
        defaultFilter={defaultFilter}
        onFinish={handleOkFilter}
        onCancel={handleCancelFilter}
      />
      <TablePaymentInfo
        ref={childRef}
        data={data || {}}
        loading={mutateGetReport.isPending}
        refetch={refetchData}
      />
    </>
  );
};

export default PaymentInfoPage;
